import { Component, HostListener, OnInit } from '@angular/core';
import { ApplicationsService } from 'app/services/applications.service';
import { APPLICATION_TYPE_LABELS, ApplicationType, ProjectFormData } from 'app/models/application';
import { TOPIC_GROUPS } from 'app/models/epe-topics';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { SessionStorage } from 'app/storage/session.storage';
import { SopStorage } from 'app/storage/sop.storage';
import { Logger } from 'app/utilities/logger';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';

enum ProjectStatusFilterEnum {
  all,
  draft,
  pending,
  eligible,
  not_eligible,
  proactive
}
interface exportProject {
  [key: string]: string;
}

const SERVICE_TYPES: ApplicationType[] = ['service1', 'service2'];

@Component({
  selector: 'projects',
  templateUrl: './projects.component.html',
  styleUrls: ['../epes/epes.component.scss', '../association/association.component.scss', './projects.component.scss', '../sop.component.scss']
})
export class ProjectsComponent implements OnInit {
  loading: boolean = true;
  projects: any[] | null = [];
  selected: string = '';
  ProjectStatusFilter = ProjectStatusFilterEnum;
  filter: ProjectStatusFilterEnum = ProjectStatusFilterEnum.all;

  activeService: ApplicationType = 'service1';
  readonly SERVICE_TYPES = SERVICE_TYPES;
  readonly SERVICE_LABELS = APPLICATION_TYPE_LABELS;

  screenWidth: number = window.innerWidth;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.screenWidth = window.innerWidth;
  }

  sortBy: string = 'created_at';
  sortDirection: number = -1;

  changeSort(property: string) {
    if (this.sortBy === property) {
      this.sortDirection = this.sortDirection * -1;
    } else {
      this.sortBy = property;
      this.sortDirection = 1;
    }
    this.projects = this.sortProjects();
  }

  showAlert: boolean = false;
  alertMessage: string = '';
  alertType: Alert = Alert.SUCCESS;

  search?: string;

  constructor(
    private cache: SopStorage,
    private auth: SessionStorage,
    private logger: Logger,
    private applicationsService: ApplicationsService,
  ) {}

  ngOnInit(): void {
    this.projects = this.cache.getProjects();
    if (!this.projects) {
      this.applicationsService.getAll({ include_user: true }).subscribe(
        applications => {
          this.projects = applications
            .filter(a => ['service1', 'service2', 'service3'].includes(a.application_type!))
            .map((app: any) => ({
              ...(app.form_data || {}),
              ...(app.user_info || {}),
              ...app,
              id: app.user_id ?? app.id,
              application_id: app._id?.['$oid'] ?? app._id,
              sop_status: app.status,
            }));
          this.logger.info(this.projects);
          this.cache.setProjects(this.projects);
          this.projects = this.sortProjects();
          this.loading = false;
        }
      );
    } else {
      this.loading = false;
    }
  }

  exportAuthorized() {
    return this.auth.isAdmin() || this.auth.isCallManager() || this.auth.isProjectBoard();
  }

  stripHtmlTags(content: string): string {
    const div = document.createElement('div');
    div.innerHTML = content;
    return div.textContent || div.innerText || '';
  }

  exportCSV() {
    let data = [];
    for (let project of this.projects!.filter(p => p.application_type === this.activeService)) {
      let newProject: exportProject = {};
      newProject['Applied at'] = this.formatDate(project['created_at']);
      newProject['Application Type'] = project['application_type'] || '';
      newProject['SOP Status'] = project['status'] || project['sop_status'] || '';

      newProject['Username'] = project['username'] || '';
      newProject['Title'] = project['title'] || '';
      newProject['Name(s)'] = project['name'] || '';
      newProject['Surname'] = project['surname'] || '';
      newProject['Email'] = project['email'] || '';
      newProject['Phone Number'] = project['phone_number'] || '';
      newProject['Gender'] = project['gender'] || '';
      newProject['Country'] = project['country'] || '';
      newProject['Organisation'] = project['organisation'] || '';
      newProject['Organisation type'] = project['organisation_type'] || '';

      newProject['Funding Type'] = project['funding_type'] || '';
      newProject['Grant Agreement'] = project['grant_agreement'] || '';
      newProject['Project Acronym'] = project['project_acronym'] || '';
      newProject['Full Project name'] = project['full_project_name'] || '';
      newProject['Funding Programme'] = project['funding_programme'] || '';
      newProject['Other Funding Programme'] = project['other_funding_programme'] || '';
      newProject['National Programme name'] = project['national_programme_name'] || '';
      newProject['National Programme country'] = project['national_programme_country'] || '';
      newProject['Coordinator country'] = project['coordinator_country'] || '';
      newProject['Project Call Topic ID'] = project['project_call_topic_id'] || '';
      newProject['Project Website'] = project['project_website'] || '';
      newProject['Project Officer name'] = project['project_officer_name'] || '';
      newProject['Project Officer email'] = project['project_officer_email'] || '';
      newProject['Project short description'] = project['project_short_description'] ? this.stripHtmlTags(project['project_short_description']) : '';
      newProject['Why applying'] = project['why_the_project_applying'] ? this.stripHtmlTags(project['why_the_project_applying']) : '';
      newProject['Website Publication'] = project['website_publication'] ? 'true' : 'false';

      newProject['Topics'] = this.getTopicLabels(project).join('\n');
      newProject['Information to attain'] = Array.isArray(project['information_to_attain']) ? project['information_to_attain'].join('\n') : '';
      newProject['Other support specification'] = project['other_support_specification'] ? this.stripHtmlTags(project['other_support_specification']) : '';

      // PSS (service2) only
      newProject['Support expectations'] = project['support_expectations'] ? this.stripHtmlTags(project['support_expectations']) : '';
      newProject['1-pager filename'] = project['one_pager_filename'] || '';
      newProject['TRL starting point'] = project['trl_start'] || '';
      newProject['Expected TRL after project'] = project['trl_target'] || '';
      newProject['Applicable regulations'] = project['applicable_regulations'] ? this.stripHtmlTags(project['applicable_regulations']) : '';
      newProject['Call deadline'] = this.formatDate(project['call_deadline']);

      newProject['Support start'] = project['support_start'] || '';
      newProject['Scheduling constraints'] = project['scheduling_constraints'] ? this.stripHtmlTags(project['scheduling_constraints']) : '';
      newProject['Preferred language(s)'] = project['preferred_languages'] || '';
      newProject['Time zone'] = project['time_zone'] || '';

      newProject['Cluster support'] = project['cluster_support'] || '';
      newProject['Cluster details'] = project['cluster_details'] ? this.stripHtmlTags(project['cluster_details']) : '';

      newProject['Know standards'] = project['know_standards'] === 'yes' ? 'Yes' : (project['know_standards'] === 'no' ? 'No' : (project['know_standards'] ? this.stripHtmlTags(project['know_standards']) : ''));
      newProject['Standards details'] = project['standards_details'] ? this.stripHtmlTags(project['standards_details']) : '';
      newProject['Standard references'] = this.serializeList(project['standard_reference']);
      newProject['NSB contact'] = project['nsb_contact'] === 'yes' ? 'Yes' : (project['nsb_contact'] === 'no' ? 'No' : '');
      newProject['NSB contact name'] = project['nsb_contact_name'] || '';
      newProject['Knows future standards'] = project['known_standards_yn'] === 'yes' ? 'Yes' : (project['known_standards_yn'] === 'no' ? 'No' : '');
      newProject['Known standards titles'] = project['known_standards_titles'] || '';

      newProject['Objectives standardisation'] = project['objectives_standardisation'] ? this.stripHtmlTags(project['objectives_standardisation']) : '';
      newProject['Related documents'] = this.serializeList(project['related_doc_application']);

      newProject['Data processing consent'] = project['data_consent'] ? 'Agreed' : '';
      newProject['Accuracy declaration'] = project['accuracy_declaration'] ? 'Agreed' : '';

      data.push(newProject);
    }

    const keys = [
      'Applied at', 'Application Type', 'SOP Status',
      'Username', 'Title', 'Name(s)', 'Surname', 'Email', 'Phone Number',
      'Gender', 'Country', 'Organisation', 'Organisation type',
      'Funding Type', 'Grant Agreement', 'Project Acronym', 'Full Project name',
      'Funding Programme', 'Other Funding Programme',
      'National Programme name', 'National Programme country', 'Coordinator country',
      'Project Call Topic ID',
      'Project Website', 'Project Officer name', 'Project Officer email', 'Project short description',
      'Why applying', 'Website Publication',
      'Topics', 'Information to attain', 'Other support specification',
      'Support expectations', '1-pager filename', 'TRL starting point', 'Expected TRL after project',
      'Applicable regulations', 'Call deadline',
      'Support start', 'Scheduling constraints', 'Preferred language(s)', 'Time zone',
      'Cluster support', 'Cluster details',
      'Know standards', 'Standards details', 'Standard references',
      'NSB contact', 'NSB contact name', 'Knows future standards', 'Known standards titles',
      'Objectives standardisation', 'Related documents',
      'Data processing consent', 'Accuracy declaration',
    ];

    new AngularCsv(data, 'Project-HSBooster', {
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'Applications',
      useBom: true,
      noDownload: false,
      headers: keys,
    });
  }

  private formatDate(value: any): string {
    if (!value) return ''
    const str = String(value)
    const date = /^\d+$/.test(str) ? new Date(+str * 1000) : new Date(str)
    if (isNaN(date.getTime())) return ''
    const pad = (n: number) => ('0' + n).slice(-2)
    return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  private serializeList(value: any): string {
    if (!value) return ''
    if (!Array.isArray(value)) return String(value)
    return value.map((item: any) => {
      if (item == null) return ''
      if (typeof item === 'string') return item
      try { return JSON.stringify(item) } catch { return String(item) }
    }).join('\n')
  }

  changeFilter(newStatus: ProjectStatusFilterEnum) {
    this.filter = newStatus;
  }

  setService(type: ApplicationType): void {
    this.activeService = type;
    this.filter = ProjectStatusFilterEnum.all;
    this.search = '';
    this.selected = '';
  }

  getAppId(project: any): string {
    return project._id?.['$oid'] ?? project._id ?? String(project.id ?? '');
  }

  getTopicLabels(project: any): string[] {
    const topics: string[] = project.form_data?.topics || project.topics || [];
    const labels: string[] = [];
    for (const value of topics) {
      if (typeof value !== 'string') continue;
      let found = false;
      for (const group of TOPIC_GROUPS) {
        for (const sub of group.subGroups) {
          if (sub.value === value) { labels.push(`${group.label} > ${sub.label}`); found = true; break; }
          const child = sub.children.find(c => c.value === value);
          if (child) { labels.push(`${group.label} > ${sub.label} > ${child.label}`); found = true; break; }
        }
        if (found) break;
      }
      if (!found) labels.push(value);
    }
    return labels;
  }

  getStatusLabel(status: string = ''): string {
    const map: Record<string, string> = {
      draft: 'Draft', pending: 'Pending', eligible: 'Eligible',
      not_eligible: 'Not Eligible', proactive: 'Proactive',
    };
    return map[status] || status;
  }

  getStatusBadgeClass(status: string = ''): string {
    const map: Record<string, string> = {
      draft: 'label-default', pending: 'label-warning', eligible: 'label-success',
      not_eligible: 'label-danger', proactive: 'label-primary',
    };
    return map[status] || 'label-default';
  }

  getBtnText(status: string = '') {
    switch (status) {
      case 'pending': return 'Eligibility Check';
      default: return 'View details';
    }
  }

  getProjects(): any[] {
    if (!this.projects) return [];
    let list = this.projects.filter(p => p.application_type === this.activeService);
    if (this.search && this.search.length > 0) {
      const q = this.search.toLowerCase();
      list = list.filter(x =>
        (x.username ?? '').toLowerCase().includes(q) ||
        (x.project_acronym ?? '').toLowerCase().includes(q) ||
        ((x.name ?? '') + ' ' + (x.surname ?? '')).toLowerCase().includes(q) ||
        (x.email ?? '').toLowerCase().includes(q) ||
        (x.organisation ?? '').toLowerCase().includes(q)
      );
    }
    switch (this.filter) {
      case ProjectStatusFilterEnum.draft:        return list.filter(x => x.status === 'draft');
      case ProjectStatusFilterEnum.pending:      return list.filter(x => x.status === 'pending');
      case ProjectStatusFilterEnum.eligible:     return list.filter(x => x.status === 'eligible');
      case ProjectStatusFilterEnum.not_eligible: return list.filter(x => x.status === 'not_eligible');
      case ProjectStatusFilterEnum.proactive:    return list.filter(x => x.status === 'proactive');
      default: return list;
    }
  }

  expand(id: string = '') {
    this.selected = this.selected === id ? '' : id;
  }

  sortProjects(): any[] {
    if (!this.projects) return [];
    const dir = this.sortDirection;
    switch (this.sortBy) {
      case 'name':
        return [...this.projects].sort((a, b) => dir * (a.name ?? '').localeCompare(b.name ?? ''));
      case 'surname':
        return [...this.projects].sort((a, b) => dir * (a.surname ?? '').localeCompare(b.surname ?? ''));
      case 'projectName':
        return [...this.projects].sort((a, b) => dir * (a.full_project_name ?? '').trimStart().localeCompare((b.full_project_name ?? '').trimStart()));
      case 'acronym':
        return [...this.projects].sort((a, b) => dir * (a.project_acronym ?? '').trimStart().localeCompare((b.project_acronym ?? '').trimStart()));
      case 'created_at':
        return [...this.projects].sort((a, b) => {
          const parse = (v: any) => { const s = String(v ?? 0); return /^\d+$/.test(s) ? +s * 1000 : new Date(s).getTime(); };
          return dir * (parse(a.created_at) - parse(b.created_at));
        });
    }
    return this.projects;
  }

  addAlert(message: string, type: Alert) {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;
  }

  truncateFloat(n: number): number {
    return Math.floor(n * 100) / 100;
  }
}
